
import { useState } from 'react';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const response = await fetch('https://readdy.ai/api/form/d62u6hlanburc340q8c0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams(formData as Record<string, string>).toString(),
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ name: '', email: '', phone: '', service: '', message: '' });
      } else {
        setSubmitStatus('error');
      }
    } catch {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-16">
          <div>
            <span className="text-[#2196F3] font-semibold text-sm uppercase tracking-wider">Contact Us</span>
            <h3 className="text-3xl md:text-4xl font-bold text-[#0A2647] mt-3 mb-6">
              Get Your Free Quote Today
            </h3>
            <p className="text-gray-600 text-lg leading-relaxed mb-8">
              Ready to find the perfect health insurance plan? Contact us today for a free, 
              no-obligation consultation. Our friendly team is here to answer all your questions.
            </p>

            <div className="space-y-6">
              <div className="flex items-start gap-4">
                <div className="w-14 h-14 bg-[#2196F3]/10 rounded-xl flex items-center justify-center flex-shrink-0">
                  <i className="ri-map-pin-line text-2xl text-[#2196F3]"></i>
                </div>
                <div>
                  <h5 className="font-semibold text-[#0A2647] mb-1">Visit Our Office</h5>
                  <p className="text-gray-600">8708 S Congress Ave<br />Austin, TX 78745</p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-14 h-14 bg-[#2196F3]/10 rounded-xl flex items-center justify-center flex-shrink-0">
                  <i className="ri-phone-line text-2xl text-[#2196F3]"></i>
                </div>
                <div>
                  <h5 className="font-semibold text-[#0A2647] mb-1">Call Us</h5>
                  <a href="tel:512-306-6245" className="text-[#2196F3] font-semibold text-lg hover:text-[#1976D2] cursor-pointer">
                    512-306-6245
                  </a>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-14 h-14 bg-[#2196F3]/10 rounded-xl flex items-center justify-center flex-shrink-0">
                  <i className="ri-time-line text-2xl text-[#2196F3]"></i>
                </div>
                <div>
                  <h5 className="font-semibold text-[#0A2647] mb-1">Business Hours</h5>
                  <p className="text-gray-600">Open 7 Days a Week<br />4 Hours Daily - Call for Availability</p>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-gray-50 rounded-2xl p-8">
            <h4 className="text-2xl font-bold text-[#0A2647] mb-6">Request a Free Quote</h4>
            
            {submitStatus === 'success' && (
              <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg text-green-700">
                <i className="ri-check-line mr-2"></i>
                Thank you! We'll be in touch soon.
              </div>
            )}

            {submitStatus === 'error' && (
              <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
                <i className="ri-error-warning-line mr-2"></i>
                Something went wrong. Please try again or call us directly.
              </div>
            )}

            <form id="contact-form" data-readdy-form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">Full Name</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:border-[#2196F3] focus:ring-2 focus:ring-[#2196F3]/20 outline-none transition-all text-sm"
                  placeholder="John Smith"
                />
              </div>

              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">Email Address</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:border-[#2196F3] focus:ring-2 focus:ring-[#2196F3]/20 outline-none transition-all text-sm"
                    placeholder="john@example.com"
                  />
                </div>
                <div>
                  <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">Phone Number</label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:border-[#2196F3] focus:ring-2 focus:ring-[#2196F3]/20 outline-none transition-all text-sm"
                    placeholder="(512) 000-0000"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="service" className="block text-sm font-medium text-gray-700 mb-2">Service Interested In</label>
                <select
                  id="service"
                  name="service"
                  value={formData.service}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:border-[#2196F3] focus:ring-2 focus:ring-[#2196F3]/20 outline-none transition-all text-sm cursor-pointer"
                >
                  <option value="">Select a service</option>
                  <option value="Medicare / Over 65">Medicare / Over 65</option>
                  <option value="Under 65 Individual/Family">Under 65 Individual/Family</option>
                  <option value="Group Health for Business">Group Health for Business</option>
                </select>
              </div>

              <div>
                <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">Message (Optional)</label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  rows={4}
                  maxLength={500}
                  className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:border-[#2196F3] focus:ring-2 focus:ring-[#2196F3]/20 outline-none transition-all text-sm resize-none"
                  placeholder="Tell us about your insurance needs..."
                ></textarea>
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-[#2196F3] hover:bg-[#1976D2] disabled:bg-gray-400 text-white py-4 rounded-lg font-semibold transition-colors cursor-pointer whitespace-nowrap"
              >
                {isSubmitting ? (
                  <span className="flex items-center justify-center gap-2">
                    <i className="ri-loader-4-line animate-spin"></i>
                    Submitting...
                  </span>
                ) : (
                  'Get My Free Quote'
                )}
              </button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
